import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { playPairFailureSound } from "@/lib/floorStationPairAudio";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { cn } from "@/lib/utils";
import type { SdTestConnectionStatus, SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";

import {
  CONFIGURATION_US_CLAMP_SCAN_VALUE,
  getConfigurationFieldConfig,
  identifyConfigurationScan,
  readConfigurationPrefillValue,
  type ConfigurationDeviceType,
  validateConfigurationForm,
  validateConfigurationIccidCarrierAlignment,
  skuRequiresImeiIccid,
} from "./configurationDeviceFields";
import { ConfigurationScanDataMatrix } from "./ConfigurationScanDataMatrix";
import { ConfigurationSubmitDataMatrix } from "./ConfigurationSubmitDataMatrix";
import { renderConfigurationSubmitFailureMessage } from "./configurationSubmitFailureMessage";
import {
  CONFIGURATION_SUBMIT_FAILED_LABEL,
  configurationSubmitFailureButtonLabel,
} from "./configurationSubmitFailure";
import type { ConfigurationBuiltUnit } from "./configurationApi";
import {
  buildConfigurationFormSupportContext,
  recordConfigurationClientValidationFailureForSupport,
} from "./configurationSupportLogContext";
import { recordHubSupportConsoleLine } from "@/lib/installHubSupportConsoleCapture";

const TOUCH_INPUT_CLASS = "h-11 text-base touch-manipulation";
const TOUCH_ACTION_CLASS = "min-h-14 touch-manipulation";
const SUBMIT_DATA_MATRIX_PX = 68;
const CLAMP_US_DATA_MATRIX_PX = 44;

type ScanStatusType = "success" | "failure" | null;

export type ConfigurationDeviceFormHandle = {
  clearFormAndFocus: () => void;
  focusScanner: () => void;
  prefillForRescan: (unit: ConfigurationBuiltUnit) => void;
  readFieldValue: (fieldName: string) => string;
  /** Copy PCBA and clamp serials from device lookup into the active configuration form. */
  applyLookupSerials: (input: { pcbaSerial: string | null; clampSerial: string | null }) => void;
  /** Clears stale submit-bar errors (for example after SDTestApp reconnect). */
  clearSubmitErrors: () => void;
};

export const ConfigurationDeviceForm = forwardRef<
  ConfigurationDeviceFormHandle,
  {
    deviceType: ConfigurationDeviceType;
    activeDevice: SdTestDevice | null;
    connection: SdTestConnectionStatus;
    activeDeviceCount: number;
    sdTestStatusText: string;
    writesAllowed: boolean;
    isSubmitting: boolean;
    /** Fields blocked by parent-side submit gates (prefix, clamp QC, open QC incident). Red border only — no focus change. */
    submitGateFieldHighlights?: Record<string, boolean>;
    /** Parent-side gates (prefix, clamp QC, open QC incident, batch cap, MCU duplicate). Shown on the submit button. */
    submitGateReason?: string;
    /** Job + MCU context for hub support console capture when submit is blocked client-side. */
    configureSupportLogContext?: {
      finishedSku?: string | null;
      batchItemId?: number | null;
      alternateOrderId?: number | null;
      rescanSerialFciId?: number | null;
      mcuUuid?: string | null;
    };
    /** Finished SKU for wireless ICCID carrier alignment checks. */
    finishedSku?: string;
    submitSuccessMessage?: string | null;
    rescanArmed?: boolean;
    rescanDeviceLabel?: string | null;
    onFormCleared?: () => void;
    onScannerInput?: () => void;
    onFieldChange?: (fieldName: string, value: string) => void;
    onSubmit: (formData: Record<string, string>) => Promise<void>;
  }
>(function ConfigurationDeviceForm(
  {
    deviceType,
    activeDevice,
    connection,
    activeDeviceCount,
    sdTestStatusText,
    writesAllowed,
    isSubmitting,
    submitGateFieldHighlights = {},
    submitGateReason = "",
    configureSupportLogContext,
    finishedSku = "",
    submitSuccessMessage = null,
    rescanArmed = false,
    rescanDeviceLabel = null,
    onFormCleared,
    onScannerInput,
    onFieldChange,
    onSubmit,
  },
  ref,
): JSX.Element {
  const fieldConfig = getConfigurationFieldConfig(deviceType);
  const fieldNames = Object.keys(fieldConfig);

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [scanValue, setScanValue] = useState("");
  const [scanStatus, setScanStatus] = useState("");
  const [scanStatusType, setScanStatusType] = useState<ScanStatusType>(null);
  const [submitFailureReason, setSubmitFailureReason] = useState("");
  const [submitFailureButtonLabel, setSubmitFailureButtonLabel] = useState("");
  const [fieldHighlight, setFieldHighlight] = useState<Record<string, boolean>>({});

  const touchedFieldsRef = useRef<Set<string>>(new Set());
  const scannerRef = useRef<HTMLInputElement>(null);
  const processScanRef = useRef<(raw: string) => void>(() => {});
  useFloorPageScanCapture({
    enabled: writesAllowed && fieldNames.length > 0,
    inputRef: scannerRef,
    onScan: (serial) => processScanRef.current(serial),
  });
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const fieldHighlightTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const scanStatusClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousConnectorIdRef = useRef("");
  const didInitialScannerFocusRef = useRef(false);

  const connectorId = String(
    activeDevice?.connectorId ?? activeDevice?.cableSerialNumber ?? activeDevice?.serialNumber ?? "",
  );

  const iccidCarrierAlignmentError = useCallback(
    (iccid: string): string | null => {
      if (!skuRequiresImeiIccid(finishedSku)) return null;
      const result = validateConfigurationIccidCarrierAlignment(iccid, finishedSku);
      return result.error;
    },
    [finishedSku],
  );

  const applyIccidFieldError = useCallback(
    (iccid: string) => {
      const carrierError = iccidCarrierAlignmentError(iccid);
      setFieldErrors((previous) => {
        const next = { ...previous };
        if (carrierError) {
          next.iccid = carrierError;
        } else {
          delete next.iccid;
        }
        return next;
      });
      return carrierError;
    },
    [iccidCarrierAlignmentError],
  );

  const setScanStatusWithClear = useCallback((message: string, type: ScanStatusType) => {
    if (scanStatusClearTimeoutRef.current) {
      clearTimeout(scanStatusClearTimeoutRef.current);
      scanStatusClearTimeoutRef.current = null;
    }
    setScanStatus(message);
    setScanStatusType(type);
    if (type === "success" && message) {
      scanStatusClearTimeoutRef.current = setTimeout(() => {
        setScanStatus("");
        setScanStatusType(null);
        scanStatusClearTimeoutRef.current = null;
      }, 4000);
    }
  }, []);

  const flashFieldFilled = useCallback((fieldName: string) => {
    if (fieldHighlightTimersRef.current[fieldName]) {
      clearTimeout(fieldHighlightTimersRef.current[fieldName]);
    }
    setFieldHighlight((previous) => ({ ...previous, [fieldName]: true }));
    fieldHighlightTimersRef.current[fieldName] = setTimeout(() => {
      delete fieldHighlightTimersRef.current[fieldName];
      setFieldHighlight((previous) => {
        if (!previous[fieldName]) return previous;
        const next = { ...previous };
        delete next[fieldName];
        return next;
      });
    }, 2000);
  }, []);

  /**
   * Clears every scanned serial. Intended after a failed submit (client validation or API):
   * the operator must rescan from a clean form so a rejected barcode cannot remain in the
   * wrong field or be resubmitted by accident. Do not treat this wipe as a product bug.
   */
  const clearSerialFields = useCallback(() => {
    const cleared: Record<string, string> = {};
    for (const fieldName of fieldNames) {
      cleared[fieldName] = "";
    }
    touchedFieldsRef.current = new Set();
    setFormData(cleared);
    setFieldErrors({});
    setFieldHighlight({});
    Object.values(fieldHighlightTimersRef.current).forEach(clearTimeout);
    fieldHighlightTimersRef.current = {};
    onFormCleared?.();
  }, [fieldNames, onFormCleared]);

  const clearForm = useCallback(() => {
    clearSerialFields();
    setSubmitFailureReason("");
  }, [clearSerialFields]);

  useImperativeHandle(
    ref,
    () => ({
      clearFormAndFocus: () => {
        clearForm();
        setScanStatusWithClear("Form cleared", "success");
        scannerRef.current?.focus();
      },
      focusScanner: () => {
        scannerRef.current?.focus();
      },
      prefillForRescan: (unit: ConfigurationBuiltUnit) => {
        const next: Record<string, string> = {};
        for (const fieldName of fieldNames) {
          next[fieldName] = "";
        }
        if (fieldNames.includes("pcbaSerial")) {
          next.pcbaSerial = unit.pcbaSerial ?? "";
        }
        if (fieldNames.includes("clampSerial")) {
          next.clampSerial = unit.clampSerial ?? "";
        }
        if (fieldNames.includes("deviceLabelSerial")) {
          next.deviceLabelSerial = unit.deviceLabelSerial;
        }
        if (fieldNames.includes("imei")) {
          next.imei = unit.imei ?? "";
        }
        if (fieldNames.includes("iccid")) {
          next.iccid = unit.iccid ?? "";
        }
        touchedFieldsRef.current = new Set(fieldNames);
        setFormData(next);
        setFieldErrors({});
        if (fieldNames.includes("iccid")) {
          applyIccidFieldError(next.iccid ?? "");
        }
        setScanStatusWithClear(`Rescan ${unit.deviceLabelSerial} — update serials and submit`, "success");
        scannerRef.current?.focus();
      },
      readFieldValue: (fieldName: string) => String(formData[fieldName] ?? "").trim(),
      applyLookupSerials: (input: { pcbaSerial: string | null; clampSerial: string | null }) => {
        const pcbaSerial = (input.pcbaSerial ?? "").trim();
        const clampSerial = (input.clampSerial ?? "").trim();
        if (pcbaSerial === "" && clampSerial === "") {
          setScanStatusWithClear("Device lookup has no PCBA or clamp serial to copy", "failure");
          return;
        }
        setFormData((previous) => {
          const next = { ...previous };
          if (fieldNames.includes("pcbaSerial") && pcbaSerial !== "") {
            next.pcbaSerial = pcbaSerial;
            touchedFieldsRef.current.add("pcbaSerial");
            flashFieldFilled("pcbaSerial");
          }
          if (fieldNames.includes("clampSerial") && clampSerial !== "") {
            next.clampSerial = clampSerial;
            touchedFieldsRef.current.add("clampSerial");
            flashFieldFilled("clampSerial");
          }
          return next;
        });
        setSubmitFailureReason("");
        const copied: string[] = [];
        if (pcbaSerial !== "" && fieldNames.includes("pcbaSerial")) copied.push("PCBA");
        if (clampSerial !== "" && fieldNames.includes("clampSerial")) copied.push("clamp");
        setScanStatusWithClear(
          copied.length > 0 ? `Copied ${copied.join(" and ")} serial from device lookup` : "Nothing copied",
          copied.length > 0 ? "success" : "failure",
        );
        scannerRef.current?.focus();
      },
      clearSubmitErrors: () => {
        setSubmitFailureReason("");
        setSubmitFailureButtonLabel("");
        setFieldErrors({});
      },
    }),
    [clearForm, fieldNames, flashFieldFilled, formData, setScanStatusWithClear, applyIccidFieldError],
  );

  useEffect(() => {
    if (fieldNames.length === 0 || didInitialScannerFocusRef.current) return;
    didInitialScannerFocusRef.current = true;
    scannerRef.current?.focus();
  }, [fieldNames.length]);

  useEffect(() => {
    setSubmitFailureReason("");
  }, [submitGateReason]);

  useEffect(() => {
    if (connectorId === "" || connectorId === previousConnectorIdRef.current) return;
    previousConnectorIdRef.current = connectorId;
    touchedFieldsRef.current = new Set();
    setFormData({});
    setFieldErrors({});
  }, [connectorId]);

  useEffect(() => {
    if (!activeDevice || fieldNames.length === 0) return;
    setFormData((previous) => {
      let changed = false;
      const next = { ...previous };
      for (const fieldName of fieldNames) {
        if (touchedFieldsRef.current.has(fieldName)) continue;
        if (String(previous[fieldName] ?? "").trim() !== "") continue;
        const fromDevice = readConfigurationPrefillValue(fieldName, activeDevice);
        if (fromDevice !== "") {
          next[fieldName] = fromDevice;
          changed = true;
        }
      }
      if (changed && fieldNames.includes("iccid")) {
        applyIccidFieldError(next.iccid ?? "");
      }
      return changed ? next : previous;
    });
  }, [activeDevice, applyIccidFieldError, fieldNames]);

  useEffect(() => {
    if (!fieldNames.includes("iccid")) return;
    applyIccidFieldError(formData.iccid ?? "");
  }, [applyIccidFieldError, fieldNames, finishedSku, formData.iccid]);

  const handleFieldChange = (fieldName: string, value: string) => {
    touchedFieldsRef.current.add(fieldName);
    setFormData((previous) => ({ ...previous, [fieldName]: value }));
    setSubmitFailureReason("");
    onFieldChange?.(fieldName, value);
    if (fieldName === "iccid") {
      applyIccidFieldError(value);
    } else {
      setFieldErrors((previous) => {
        if (!previous[fieldName]) return previous;
        const next = { ...previous };
        delete next[fieldName];
        return next;
      });
    }
    if (String(value).trim() !== "") {
      flashFieldFilled(fieldName);
    }
  };

  const getSubmitDisabledReason = useCallback((): string => {
    if (isSubmitting) return "Submitting… please wait";
    if (!writesAllowed) return "Read-only — submitting is disabled";
    if (fieldNames.length === 0) return "SKU family is not supported for configuration";
    if (connection !== "connected") {
      return sdTestStatusText || "SDTestApp is not connected";
    }
    if (activeDeviceCount !== 1) {
      return activeDeviceCount === 0
        ? "SDTestApp is connected but no active device is present. Connect exactly one device."
        : `SDTestApp reports ${activeDeviceCount} active devices. Connect exactly one device before submitting.`;
    }
    if (sdTestStatusText !== "SDTestApp Connected") {
      return sdTestStatusText || "SDTestApp status unavailable";
    }
    if (submitGateReason.trim() !== "") return submitGateReason.trim();
    if (submitFailureReason.trim() !== "") return submitFailureReason.trim();
    return "";
  }, [
    activeDeviceCount,
    connection,
    fieldNames.length,
    isSubmitting,
    sdTestStatusText,
    submitFailureReason,
    submitGateReason,
    writesAllowed,
  ]);

  const handleSubmitInternal = useCallback(async () => {
    if (
      submitSuccessMessage != null &&
      submitSuccessMessage.trim() !== "" &&
      !isSubmitting
    ) {
      return;
    }

    const disabledReason = getSubmitDisabledReason();
    if (disabledReason) {
      if (submitGateReason.trim() !== "" && disabledReason === submitGateReason.trim()) {
        recordHubSupportConsoleLine("error", {
          event: "configuration_submit_blocked",
          reason: disabledReason,
          device: buildConfigurationFormSupportContext({
            formData,
            finishedSku: configureSupportLogContext?.finishedSku,
            batchItemId: configureSupportLogContext?.batchItemId,
            alternateOrderId: configureSupportLogContext?.alternateOrderId,
            rescanSerialFciId: configureSupportLogContext?.rescanSerialFciId,
            mcuUuid: configureSupportLogContext?.mcuUuid,
          }),
        });
      }
      playPairFailureSound();
      return;
    }

    const validation = validateConfigurationForm(formData, fieldConfig);
    if (!validation.isValid) {
      playPairFailureSound();
      const firstField = Object.keys(validation.errors)[0];
      const firstError =
        (firstField ? validation.errors[firstField] : null) ??
        "Fix validation errors before submitting";
      recordConfigurationClientValidationFailureForSupport({
        errors: validation.errors,
        formData,
        finishedSku: configureSupportLogContext?.finishedSku,
        batchItemId: configureSupportLogContext?.batchItemId,
        alternateOrderId: configureSupportLogContext?.alternateOrderId,
        rescanSerialFciId: configureSupportLogContext?.rescanSerialFciId,
        mcuUuid: configureSupportLogContext?.mcuUuid,
      });
      // Intended wipe: rejected barcode must not stay in a field after validation failure.
      clearSerialFields();
      setSubmitFailureReason(firstError);
      scannerRef.current?.focus();
      return;
    }

    const iccidCarrierError = iccidCarrierAlignmentError(formData.iccid ?? "");
    if (iccidCarrierError) {
      playPairFailureSound();
      recordConfigurationClientValidationFailureForSupport({
        errors: { iccid: iccidCarrierError },
        formData,
        finishedSku: configureSupportLogContext?.finishedSku,
        batchItemId: configureSupportLogContext?.batchItemId,
        alternateOrderId: configureSupportLogContext?.alternateOrderId,
        rescanSerialFciId: configureSupportLogContext?.rescanSerialFciId,
        mcuUuid: configureSupportLogContext?.mcuUuid,
      });
      applyIccidFieldError(formData.iccid ?? "");
      setSubmitFailureReason(iccidCarrierError);
      scannerRef.current?.focus();
      return;
    }

    try {
      await onSubmit(formData);
      clearForm();
      scannerRef.current?.focus();
    } catch (error) {
      playPairFailureSound();
      // Intended wipe: API failure must not leave scanned serials on the form for accidental resubmit.
      clearSerialFields();
      const failureLabel = configurationSubmitFailureButtonLabel(error);
      setSubmitFailureButtonLabel(
        failureLabel !== "" ? failureLabel : CONFIGURATION_SUBMIT_FAILED_LABEL,
      );
      setSubmitFailureReason(error instanceof Error ? error.message : CONFIGURATION_SUBMIT_FAILED_LABEL);
      scannerRef.current?.focus();
    }
  }, [
    clearForm,
    clearSerialFields,
    configureSupportLogContext,
    fieldConfig,
    formData,
    getSubmitDisabledReason,
    iccidCarrierAlignmentError,
    applyIccidFieldError,
    isSubmitting,
    onSubmit,
    submitGateReason,
    submitSuccessMessage,
  ]);

  const processScan = useCallback(
    (rawValue: string) => {
      const trimmed = String(rawValue ?? "")
        .trim()
        .replace(/[\r\n]+/g, "");
      if (trimmed === "") return;

      onScannerInput?.();
      setSubmitFailureReason("");
      setSubmitFailureButtonLabel("");

      const scanResult = identifyConfigurationScan(trimmed, fieldConfig);
      switch (scanResult.type) {
        case "SUBMIT":
          if (isSubmitting) {
            return;
          }
          void handleSubmitInternal();
          break;
        case "RESET":
          clearForm();
          setScanStatusWithClear("Form cleared", "success");
          scannerRef.current?.focus();
          break;
        case "FIELD":
          if (scanResult.fieldName) {
            handleFieldChange(scanResult.fieldName, trimmed);
            const label = fieldConfig[scanResult.fieldName]?.label ?? scanResult.fieldName;
            if (scanResult.fieldName === "iccid") {
              const carrierError = iccidCarrierAlignmentError(trimmed);
              if (carrierError) {
                playPairFailureSound();
                setScanStatusWithClear(carrierError, "failure");
                scannerRef.current?.focus();
                break;
              }
            }
            setScanStatusWithClear(`${label} detected — auto-filled`, "success");
            scannerRef.current?.focus();
          }
          break;
        default:
          setScanStatusWithClear(`Unknown scan format: ${trimmed}`, "failure");
          break;
      }
    },
    [
      clearForm,
      fieldConfig,
      handleSubmitInternal,
      iccidCarrierAlignmentError,
      isSubmitting,
      onScannerInput,
      setScanStatusWithClear,
    ],
  );

  processScanRef.current = processScan;

  const submitDisabledReason = getSubmitDisabledReason();
  const submitFailureActive = submitFailureReason.trim() !== "";
  const submitSuccessActive =
    !isSubmitting && submitSuccessMessage != null && submitSuccessMessage.trim() !== "";
  const submitBlockActive =
    !isSubmitting &&
    !submitSuccessActive &&
    submitDisabledReason !== "" &&
    submitDisabledReason !== "Submitting… please wait";
  const submitInteractionDisabled = submitSuccessActive || submitDisabledReason !== "";
  const submitButtonLabel = isSubmitting
    ? rescanArmed
      ? "Rescanning…"
      : "Submitting…"
    : submitSuccessActive
      ? submitSuccessMessage.trim()
      : submitFailureActive
        ? submitFailureButtonLabel.trim() !== ""
          ? submitFailureButtonLabel.trim()
          : CONFIGURATION_SUBMIT_FAILED_LABEL
        : submitDisabledReason !== ""
          ? submitDisabledReason.replace(/^Submit disabled:\s*/i, "")
          : rescanArmed
            ? rescanDeviceLabel != null && rescanDeviceLabel.trim() !== ""
              ? `Rescan ${rescanDeviceLabel.trim()}`
              : "Rescan: update serials"
            : "Submit configuration";
  const submitButtonLongCopy = submitButtonLabel.length > 36;
  const submitRescanActive = rescanArmed && !submitSuccessActive;

  return (
    <div className="space-y-3">
      {fieldNames.length === 0 ? (
        <p className="text-sm text-amber-700">
          This SKU is not in a supported Configure family (Patrolman Plus, Patrolman, Recon, Recon Relay).
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <Label
              htmlFor="configuration-scanner"
              className="text-sm font-medium"
            >
              Scan barcode or Data Matrix
            </Label>
            <Input
                id="configuration-scanner"
                ref={scannerRef}
                value={scanValue}
                autoComplete="off"
                placeholder="Point scanner here…"
                className={TOUCH_INPUT_CLASS}
                {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                onChange={(event) => {
                  const value = event.target.value;
                  setScanValue(value);
                  if (event.type === "paste" || value.includes("\n") || value.includes("\r")) {
                    const clean = value.trim().replace(/[\r\n]+/g, "");
                    processScan(clean);
                    setScanValue("");
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    processScan(scanValue.trim().replace(/[\r\n]+/g, ""));
                    setScanValue("");
                  }
                }}
              />
              <p
                className={cn(
                  "min-h-[1.125rem] text-sm leading-snug",
                  scanStatusType === "success" && "text-green-700",
                  scanStatusType === "failure" && "text-red-700",
                  scanStatusType == null && "text-slate-500",
                )}
              >
                {scanStatus || "Scan serials into fields, then tap Submit or scan the submit Data Matrix."}
            </p>
          </div>

          <div className="space-y-3">
            {fieldNames.map((fieldName, index) => {
              const config = fieldConfig[fieldName];
              const value = formData[fieldName] ?? "";
              const error = fieldErrors[fieldName];
              const gateHighlight = submitGateFieldHighlights[fieldName] === true;
              const showFieldError = error != null && error !== "";
              return (
                <div key={fieldName}>
                  <Label htmlFor={`configuration-field-${fieldName}`} className="text-sm font-medium">
                    {config.label}
                    {config.required ? <span className="text-red-600"> *</span> : null}
                  </Label>
                  {fieldName === "clampSerial" ? (
                    <div className="flex items-stretch gap-2">
                      <Input
                        id={`configuration-field-${fieldName}`}
                        ref={(element) => {
                          inputRefs.current[fieldName] = element;
                        }}
                        value={value}
                        autoComplete="off"
                        inputMode="text"
                        placeholder={`Enter ${config.label.toLowerCase()}`}
                        className={cn(
                          TOUCH_INPUT_CLASS,
                          "min-w-0 flex-1",
                          (showFieldError || gateHighlight) && "border-red-300 bg-red-50 ring-red-200",
                          !showFieldError && !gateHighlight && fieldHighlight[fieldName] && "border-green-400 bg-green-50",
                        )}
                        onChange={(event) => handleFieldChange(fieldName, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            const nextField = fieldNames[index + 1];
                            if (nextField && inputRefs.current[nextField]) {
                              inputRefs.current[nextField]?.focus();
                            } else {
                              void handleSubmitInternal();
                            }
                          }
                        }}
                      />
                      <ConfigurationScanDataMatrix
                        text={CONFIGURATION_US_CLAMP_SCAN_VALUE}
                        sizePx={CLAMP_US_DATA_MATRIX_PX}
                        className="shrink-0 self-center"
                        ariaLabel="Set clamp serial to US for domestic clamps"
                        title="US domestic clamp"
                        onActivate={() => {
                          handleFieldChange(fieldName, CONFIGURATION_US_CLAMP_SCAN_VALUE);
                          flashFieldFilled(fieldName);
                        }}
                      />
                    </div>
                  ) : (
                    <Input
                      id={`configuration-field-${fieldName}`}
                      ref={(element) => {
                        inputRefs.current[fieldName] = element;
                      }}
                      value={value}
                      autoComplete="off"
                      inputMode="text"
                      placeholder={`Enter ${config.label.toLowerCase()}`}
                      className={cn(
                        TOUCH_INPUT_CLASS,
                        (showFieldError || gateHighlight) && "border-red-300 bg-red-50 ring-red-200",
                        !showFieldError && !gateHighlight && fieldHighlight[fieldName] && "border-green-400 bg-green-50",
                      )}
                      onChange={(event) => handleFieldChange(fieldName, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          const nextField = fieldNames[index + 1];
                          if (nextField && inputRefs.current[nextField]) {
                            inputRefs.current[nextField]?.focus();
                          } else {
                            void handleSubmitInternal();
                          }
                        }
                      }}
                    />
                  )}
                  {showFieldError ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
                </div>
              );
            })}
          </div>

          <div className="space-y-1 pt-1">
            <div className="flex overflow-hidden rounded-md border border-slate-300 shadow-sm">
              <div className="flex shrink-0 items-center justify-center border-r border-slate-300 bg-white p-1">
                <ConfigurationSubmitDataMatrix sizePx={SUBMIT_DATA_MATRIX_PX} />
              </div>
              <Button
                type="button"
                className={cn(
                  "h-auto flex-1 rounded-none border-0 px-5 py-3 font-semibold",
                  submitButtonLongCopy ? "text-sm leading-snug" : "text-lg",
                  TOUCH_ACTION_CLASS,
                  submitRescanActive &&
                    "bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-400 disabled:bg-amber-500/60",
                  !submitRescanActive &&
                    submitSuccessActive &&
                    "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-green-600/60",
                  !submitRescanActive &&
                    submitBlockActive &&
                    "bg-rose-500 text-white hover:bg-rose-600 focus:ring-rose-400 disabled:bg-rose-500/60",
                )}
                style={{ minHeight: SUBMIT_DATA_MATRIX_PX }}
                onClick={() => void handleSubmitInternal()}
                disabled={submitInteractionDisabled}
                isLoading={isSubmitting}
                title={
                  submitSuccessActive
                    ? submitSuccessMessage ?? undefined
                    : submitRescanActive
                      ? submitDisabledReason || "Rescan: update serials on the armed unit"
                      : submitBlockActive
                        ? submitDisabledReason
                        : submitDisabledReason || (rescanArmed ? "Rescan: update serials" : "Submit configuration")
                }
              >
                {submitButtonLabel}
              </Button>
            </div>
            {submitFailureActive ? (
              <p
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm leading-snug text-red-800"
                role="alert"
              >
                {renderConfigurationSubmitFailureMessage(submitFailureReason.trim())}
              </p>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
});
ConfigurationDeviceForm.displayName = "ConfigurationDeviceForm";
